import { useEssentials } from 'hooks/utils/useEssentials'
import { TRANSLATION_KEYS } from 'locales/translation-keys'
import useRefresh from 'hooks/utils/useRefresh'
import { useDirectorSummary } from 'hooks/insights/useDirectorSummary'
import { InsightsPlaceholder } from 'pages/insights/insights-section/insights-placeholder'
import type { DirectorSummaryResponse } from 'types/insights'
import { Button, Grid } from '@design-systems/canvas-ui'
import { useNavigate } from 'react-router-dom'
import { useLogClick } from '../../utils/analytics'

interface UseInsightsDataResult {
    data: DirectorSummaryResponse | undefined
    renderGuard: JSX.Element | null
}

export const useInsightsData = (): UseInsightsDataResult => {
    const { t, location, setParams, params } = useEssentials()
    const isStoreLocation = params.get('locationId')?.startsWith('T') ?? false
    const { insights: insightsKeys } = TRANSLATION_KEYS
    const { data, isLoading, isError, error, refetch, remove } =
        useDirectorSummary(isStoreLocation)
    const navigate = useNavigate()
    const { logClick } = useLogClick('insights')

    useRefresh(refetch, remove, false)

    // If they are not at a store location or they are viewing a store that doesn't have data
    // show error page with button to set the location to their assigned store
    if (
        !isStoreLocation ||
        (isError && (error as Error)?.message.includes('404'))
    ) {
        return {
            data: undefined,
            renderGuard: (
                <ErrorPage
                    heading={t(insightsKeys.storeInsightsError.heading)}
                    message={t(insightsKeys.storeInsightsError.message)}
                    buttonTitle={t(insightsKeys.storeInsightsError.button)}
                    onClick={() => {
                        logClick('insights error: set_location_to_store')
                        if (location.defaultStoreId?.length) {
                            setParams((prev) => ({
                                ...prev,
                                locationId: location.defaultStoreId,
                            }))
                        } else {
                            const currentURL = window.location.pathname
                            void navigate('/location-search', {
                                state: { previousLocationPathname: currentURL },
                            })
                        }
                    }}
                />
            ),
        }
    }

    if (isError) {
        const errorMessage = (error as Error)?.message
        return {
            data: undefined,
            renderGuard: (
                <ErrorPage
                    heading={t(insightsKeys.errorHeading)}
                    message={t(insightsKeys.checkBackLater)}
                    submessage={errorMessage}
                />
            ),
        }
    }

    if (isLoading) {
        return {
            data: undefined,
            renderGuard: <InsightsPlaceholder />,
        }
    }

    return { data, renderGuard: null }
}

interface ErrorPageProps {
    heading: string
    message: string
    onClick?: () => void
    buttonTitle?: string
    submessage?: string
}
const ErrorPage: React.FC<ErrorPageProps> = ({
    heading,
    message,
    submessage,
    onClick,
    buttonTitle,
}) => {
    return (
        <Grid.Container
            direction="column"
            spacing="normal"
            className="hc8-wth-100 hc8-pa-4x"
            align="center"
        >
            <Grid.Item>
                <img
                    src="/insights/Store_Black_and_White.svg"
                    alt=""
                    aria-hidden="true"
                />
            </Grid.Item>
            <Grid.Item>
                <h2 className="hc8-ta-center hc8-fs-350">{heading}</h2>
            </Grid.Item>
            <Grid.Item className="hc8-fs-200 hc8-ta-center">
                {message}
            </Grid.Item>
            {submessage && (
                <Grid.Item className="hc8-fs-050 hc8-ta-center hc8-clr-base">
                    {submessage}
                </Grid.Item>
            )}
            {onClick && buttonTitle ? (
                <Grid.Item>
                    <Button variant="primary" onClick={() => onClick()}>
                        {buttonTitle}
                    </Button>
                </Grid.Item>
            ) : null}
        </Grid.Container>
    )
}
